import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import type { CategoryCode } from '@/types/domain';

import {
  fetchVacancyCandidates,
  fetchVacancyCandidatesHiddenCount,
  type VacancyCandidateParams,
  type VacancyCandidateSort,
} from '@/api/queries';
import ScoreBadge from '@/components/ScoreBadge';
import {
  APPLICATION_STATUS_LABELS,
  RECENT_APPLICATIONS_DAYS,
  applicationStatusLabel,
  isStaleInProgress,
} from '@/lib/applicationStatus';
import { candidateSourceLabel } from '@/lib/candidateSource';


const SORT_OPTIONS: Array<{ value: VacancyCandidateSort; label: string }> = [
  { value: 'score', label: 'За балом' },
  { value: 'applied_at', label: 'За датою' },
  { value: 'status', label: 'За статусом' },
];

const CATEGORY_OPTIONS: Array<{ value: CategoryCode | ''; label: string }> = [
  { value: '', label: 'Усі категорії' },
  { value: 'strong_match', label: 'Strong' },
  { value: 'good_match', label: 'Good' },
  { value: 'possible_match', label: 'Possible' },
  { value: 'low_match', label: 'Low' },
];

const PAGE_SIZE = 50;

export default function VacancyCandidatesScreen(): JSX.Element {
  const { vacancyId } = useParams();
  const [params] = useSearchParams();
  const vacancyTitle = params.get('title');

  const [sort, setSort] = useState<VacancyCandidateSort>('score');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [minScore, setMinScore] = useState('');
  const [recentOnly, setRecentOnly] = useState(true);
  const [offset, setOffset] = useState(0);

  const minScoreNum = minScore.trim() === '' ? undefined : Number(minScore);
  const queryParams: VacancyCandidateParams = {
    sort,
    status: status.trim() || undefined,
    category: category || undefined,
    min_score:
      minScoreNum !== undefined && Number.isFinite(minScoreNum) ? minScoreNum : undefined,
    recent_only: recentOnly,
    limit: PAGE_SIZE,
    offset,
  };

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['vacancy-candidates', vacancyId, queryParams],
    queryFn: () => fetchVacancyCandidates(vacancyId!, queryParams),
    enabled: !!vacancyId,
  });

  // Скільки рядків ховає сам фільтр «за 2 тижні». Порахувати на клієнті не можна:
  // список посторінковий, тому різниця двох сторінок міряла б обрізання, а не
  // сховане — і банер потрібен ДО того, як галочку знімуть.
  const hiddenParams = {
    status: queryParams.status,
    min_score: queryParams.min_score,
    category: queryParams.category,
  };
  const hiddenQuery = useQuery({
    queryKey: ['vacancy-candidates-hidden', vacancyId, hiddenParams],
    queryFn: () => fetchVacancyCandidatesHiddenCount(vacancyId!, hiddenParams),
    enabled: !!vacancyId && recentOnly,
  });

  if (!vacancyId) {
    return <div className="p-4">Невірний id вакансії.</div>;
  }

  const rows = data ?? [];
  // Скільки рядків тримає виняток «уже в роботі», а не свіжість відгуку —
  // без цього підпис обіцяє фільтр, якого на них немає.
  const staleShown = recentOnly ? rows.filter((row) => isStaleInProgress(row)).length : 0;
  const hasMore = rows.length === PAGE_SIZE;

  return (
    <div className="flex flex-col gap-3 p-4 pb-24">
      <Link to="/analytics" className="text-xs text-tg-link">
        ← До аналітики
      </Link>

      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold">Кандидати</h1>
          {vacancyTitle && <p className="truncate text-sm text-tg-hint">{vacancyTitle}</p>}
        </div>
        <Link
          to={`/vacancy/${vacancyId}/cold-search${
            vacancyTitle ? `?title=${encodeURIComponent(vacancyTitle)}` : ''
          }`}
          className="shrink-0 rounded-md bg-tg-button px-3 py-2 text-sm text-tg-buttonText"
        >
          Холодний пошук
        </Link>
      </header>

      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-xs text-tg-hint">
          Сортування
          <select
            id="vc-sort"
            name="sort"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value as VacancyCandidateSort);
              setOffset(0);
            }}
            className="rounded-md border border-tg-secondary bg-tg-bg p-2 text-sm text-tg-text"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-tg-hint">
          Категорія
          <select
            id="vc-category"
            name="category"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setOffset(0);
            }}
            className="rounded-md border border-tg-secondary bg-tg-bg p-2 text-sm text-tg-text"
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-tg-hint">
          Мін. бал
          <input
            id="vc-min-score"
            name="minScore"
            type="number"
            inputMode="decimal"
            min={0}
            max={10}
            step={0.5}
            value={minScore}
            onChange={(e) => {
              setMinScore(e.target.value);
              setOffset(0);
            }}
            placeholder="напр. 6"
            className="rounded-md border border-tg-secondary bg-tg-bg p-2 text-sm text-tg-text"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-tg-hint">
          Статус
          <select
            id="vc-status"
            name="status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
            className="rounded-md border border-tg-secondary bg-tg-bg p-2 text-sm text-tg-text"
          >
            <option value="">Усі статуси</option>
            {Object.entries(APPLICATION_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="flex items-center gap-2 text-sm text-tg-text">
        <input
          id="vc-recent-only"
          name="recentOnly"
          type="checkbox"
          checked={recentOnly}
          onChange={(e) => {
            setRecentOnly(e.target.checked);
            setOffset(0);
          }}
          className="h-4 w-4 accent-tg-button"
        />
        Лише за останні 2 тижні
      </label>
      {recentOnly && (
        <p className="-mt-1 text-xs text-tg-hint">
          Показано свіжі відгуки (≤{RECENT_APPLICATIONS_DAYS} днів)
          {staleShown > 0 &&
            ` і ще ${staleShown} на цій сторінці у роботі — таких видно завжди, хоч який давній відгук`}
          .{' '}
          {hiddenQuery.data !== undefined && hiddenQuery.data > 0 ? (
            <span className="text-tg-text">
              Усього сховано {hiddenQuery.data} — вимкніть, щоб побачити.
            </span>
          ) : (
            'Вимкніть, щоб бачити всіх.'
          )}
        </p>
      )}

      {isLoading && <div className="py-12 text-center text-tg-hint">Завантаження…</div>}
      {isError && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          Не вдалося завантажити кандидатів: {(error as Error).message}
        </div>
      )}
      {data && rows.length === 0 && (
        <div className="rounded-md bg-tg-secondary p-6 text-center text-sm text-tg-hint">
          За цими фільтрами кандидатів немає.
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {rows.map((item) => (
          <li key={item.application_id}>
            <Link
              to={`/candidate/${item.candidate_id}?vacancy=${vacancyId}`}
              className="block rounded-lg border border-tg-secondary bg-tg-bg p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-tg-text">{item.full_name}</div>
                  {item.city && <div className="truncate text-xs text-tg-hint">{item.city}</div>}
                </div>
                {item.score !== null && item.scale_max !== null ? (
                  <ScoreBadge
                    score={item.score}
                    scaleMax={item.scale_max}
                    category={item.category}
                  />
                ) : (
                  <span className="shrink-0 rounded-full bg-tg-secondary px-2 py-0.5 text-xs text-tg-hint">
                    без оцінки
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-tg-hint">
                <span className="rounded bg-tg-secondary px-2 py-0.5">
                  {candidateSourceLabel(item.source, item.peopleforce_source)}
                </span>
                <span className="rounded bg-tg-secondary px-2 py-0.5">
                  {applicationStatusLabel(item.status)}
                </span>
                {item.applied_at && (
                  <span className="ml-auto">
                    {new Date(item.applied_at).toLocaleDateString('uk-UA')}
                    {/* Чому рядок тут при увімкненій галочці: його тримає
                        виняток для взятих у роботу, а не дата відгуку. */}
                    {recentOnly && isStaleInProgress(item) && (
                      <span className="ml-1 opacity-70">
                        · старший за {RECENT_APPLICATIONS_DAYS} днів
                      </span>
                    )}
                  </span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {(offset > 0 || hasMore) && (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            disabled={offset === 0 || isFetching}
            className="rounded-md px-3 py-1 text-sm text-tg-link disabled:opacity-40"
          >
            ← Назад
          </button>
          <button
            type="button"
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            disabled={!hasMore || isFetching}
            className="rounded-md px-3 py-1 text-sm text-tg-link disabled:opacity-40"
          >
            Далі →
          </button>
        </div>
      )}
    </div>
  );
}
